/**
 * 사용자 메시지 Firestore 문서에 남길 첨부 계측 정보.
 *
 * 서버 로그에는 files=N 만 남고, 메시지 문서에는 첨부 정보가 전혀 없어
 * "파일을 올렸는데 AI가 못 읽었다"는 민원을 사후에 재구성할 수 없었다.
 * 요청에 실린 파일(ids/names)과 함께, 칩에는 있었지만 요청에 실리지 못한 파일
 * (업로드 실패·업로드 중)도 기록해 두면 어느 단계에서 빠졌는지 알 수 있다.
 *
 * @param {object} p
 * @param {Array} p.referencedFiles 이전 턴 참고 파일 칩
 * @param {Array} p.attachedFiles 이번 턴 첨부 칩
 * @param {Array} [p.extraFiles] 전송 중 만들어져 state 에 없는 첨부(유튜브 노트 등)
 * @param {Object<string,string>} [p.versionMap] hwpx file_id → 최신 수정본 id
 * @param {(f: object) => boolean} p.isContentReferenceFile 내용 참조 대상 판별
 * @returns {{
 *   attachedFileIds: string[], attachedFileNames: string[], attachmentCount: number,
 *   newlyAttachedFileIds: string[], unsentAttachmentNames: string[], unsentAttachmentReasons: string[]
 * } | null} 첨부 관련 정보가 전혀 없으면 null
 */
export function buildAttachmentMeta({
  referencedFiles = [],
  attachedFiles = [],
  extraFiles = [],
  versionMap = {},
  isContentReferenceFile,
}) {
  const upgrade = (fid) => versionMap[fid] || fid;
  const idsSeen = new Set();
  const attachedFileIds = [];
  const attachedFileNames = [];
  const pushSent = (file) => {
    const fid = upgrade(file.serverFileId);
    if (!fid || idsSeen.has(fid)) return;
    idsSeen.add(fid);
    attachedFileIds.push(fid);
    attachedFileNames.push(String(file.name || '').slice(0, 120));
  };

  const previous = referencedFiles.filter((f) => f && isContentReferenceFile(f));
  previous.forEach(pushSent);
  const previousIds = new Set(attachedFileIds);

  const current = [...attachedFiles, ...extraFiles].filter((f) => f && isContentReferenceFile(f));
  const unsentAttachmentNames = [];
  const unsentAttachmentReasons = [];
  current.forEach((file) => {
    if (file.uploaded && file.serverFileId) {
      pushSent(file);
      return;
    }
    unsentAttachmentNames.push(String(file.name || '').slice(0, 120));
    unsentAttachmentReasons.push(
      file.error ? 'upload_error' : (file.uploading || file.queued) ? 'upload_pending' : 'not_uploaded'
    );
  });

  const newlyAttachedFileIds = attachedFileIds.filter((fid) => !previousIds.has(fid));

  if (attachedFileIds.length === 0 && unsentAttachmentNames.length === 0) return null;
  return {
    attachedFileIds,
    attachedFileNames,
    attachmentCount: attachedFileIds.length,
    newlyAttachedFileIds,
    unsentAttachmentNames,
    unsentAttachmentReasons,
  };
}

/** Firestore 사용자 메시지 문서에 병합할 필드만 고른다(빈 배열은 저장하지 않음). */
export function attachmentMetaToMessageFields(meta) {
  if (!meta) return {};
  const fields = {
    attachedFileIds: meta.attachedFileIds,
    attachedFileNames: meta.attachedFileNames,
    attachmentCount: meta.attachmentCount,
  };
  if (meta.newlyAttachedFileIds.length > 0) fields.newlyAttachedFileIds = meta.newlyAttachedFileIds;
  if (meta.unsentAttachmentNames.length > 0) {
    fields.unsentAttachmentNames = meta.unsentAttachmentNames;
    fields.unsentAttachmentReasons = meta.unsentAttachmentReasons;
  }
  return fields;
}
